import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, CheckCircle2, Loader2, Paperclip, Pencil, Play, Trash2, Users, X, XCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { WhatsAppIcon } from "@/components/WhatsAppFloat";
import { api, API_URL } from "@/lib/api";
import { SERVICE_TYPES } from "@/lib/constants";
import { DemandFlow } from "./DemandFlow";
import { EvaluationBlock } from "./EvaluationBlock";
import { PricingBlock } from "./PricingBlock";
import { ExecutionBlock } from "./ExecutionBlock";
import { PartnerEvaluations } from "./PartnerEvaluations";
import {
  ARRAY_EDIT_KEYS,
  Badge,
  EDITABLE_KEYS,
  PAYLOAD_LABELS,
  PARTNER_STAGES,
  PRIORITY_COLORS,
  STAGE_ORDER,
  STATUS_LABELS,
  TYPE_META,
  displayName,
  renderValue,
} from "./shared";

const fmtSlot = (s) => `${String(s.date).split("-").reverse().join("/")} às ${s.time}`;

export const LeadDetail = ({ lead, context = "leads", onClose, onChanged }) => {
  const [matches, setMatches] = useState(null);
  const [indicating, setIndicating] = useState(null);
  const [notes, setNotes] = useState(lead?.payload?.internal_notes || "");
  const [savingNotes, setSavingNotes] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [apptDate, setApptDate] = useState("");
  const [apptTime, setApptTime] = useState("");

  useEffect(() => {
    setMatches(null);
    setEditing(null);
    setApptDate(lead?.payload?.appointment?.date || "");
    setApptTime(lead?.payload?.appointment?.time || "");
    setNotes(lead?.payload?.internal_notes || "");
  }, [lead?.id]);

  if (!lead) {
    return <Dialog open={false} onOpenChange={onClose} />;
  }

  const patch = async (body, okMsg) => {
    try {
      await api.patch(`/admin/leads/${lead.id}`, body);
      if (okMsg) toast.success(okMsg);
      onChanged();
      return true;
    } catch {
      toast.error("Falha ao atualizar.");
      return false;
    }
  };

  const updateStatus = (status) => patch({ status }, "Etapa atualizada.");
  const classify = (service_type) => patch({ service_type }, `Demanda classificada como ${service_type}.`);
  const accept = () => patch({ status: "parceria_fechada" }, "Parceria aceita!");
  const refuse = () => {
    if (window.confirm("Recusar este lead de parceria?")) {
      patch({ status: "recusado" }, "Lead de parceria recusado.");
    }
  };

  const startEdit = (key, value) => {
    setEditing(key);
    setEditValue(Array.isArray(value) ? value.join(", ") : value || "");
  };

  const saveField = async (key) => {
    const value = ARRAY_EDIT_KEYS.has(key)
      ? editValue.split(",").map((v) => v.trim()).filter(Boolean)
      : editValue;
    const ok = await patch({ fields: { [key]: value } }, "Campo atualizado.");
    if (ok) setEditing(null);
  };

  const saveAppointment = async () => {
    if (!apptDate || !apptTime) {
      toast.error("Escolha a data e o horário da execução.");
      return;
    }
    await patch(
      { appointment: { date: apptDate, time: apptTime }, status: "agendado" },
      "Execução agendada — já está na Agenda."
    );
  };

  const remove = async () => {
    if (!window.confirm("Excluir este chamado definitivamente?")) return;
    try {
      await api.delete(`/admin/leads/${lead.id}`);
      toast.success("Chamado excluído.");
      onClose();
      onChanged();
    } catch {
      toast.error("Falha ao excluir chamado.");
    }
  };

  const openAttachment = async (att) => {
    const fetchBlob = () => api.get(`/files/${att.path}`, { responseType: "blob" });
    try {
      let res;
      try {
        res = await fetchBlob();
      } catch (e) {
        if (e.response?.status === 401) {
          await api.post("/auth/refresh");
          res = await fetchBlob();
        } else {
          throw e;
        }
      }
      const blob = new Blob([res.data], { type: att.content_type });
      window.open(URL.createObjectURL(blob), "_blank", "noopener");
    } catch {
      toast.error("Não foi possível abrir o anexo.");
    }
  };

  const loadMatches = async () => {
    setMatches({ loading: true, items: [] });
    try {
      const { data } = await api.get(`/admin/leads/${lead.id}/matches`);
      const indicatedId = lead.payload?.indicated_partner?.partner_id;
      setMatches({ loading: false, items: data.filter((p) => p.id !== indicatedId) });
    } catch {
      setMatches(null);
      toast.error("Falha ao buscar parceiros compatíveis.");
    }
  };

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      await api.patch(`/admin/leads/${lead.id}`, { fields: { internal_notes: notes } });
      toast.success("Observações salvas.");
      onChanged();
    } catch {
      toast.error("Falha ao salvar observações.");
    } finally {
      setSavingNotes(false);
    }
  };

  const indicate = async (partner) => {
    if (indicating) return;
    setIndicating(partner.id);
    try {
      const { data } = await api.post("/admin/indication", {
        lead_id: lead.id,
        partner_id: partner.id,
      });
      const digits = String(partner.payload?.phone || "").replace(/\D/g, "");
      const full = digits.length <= 11 ? `55${digits}` : digits;
      window.open(`https://wa.me/${full}?text=${encodeURIComponent(data.text)}`, "_blank", "noopener");
      if (lead.status === "aguardando_indicacao") {
        await api.patch(`/admin/leads/${lead.id}`, { status: "aguardando_orcamento" }).catch(() => {});
      }
      onChanged();
      toast.success(`Indicação pronta no WhatsApp para ${partner.payload?.name}.`);
    } catch {
      toast.error("Falha ao montar a indicação.");
    } finally {
      setIndicating(null);
    }
  };

  const attachments = lead.payload?.attachments || [];
  const typeMeta = TYPE_META[lead.type] || TYPE_META.service;
  const appointment = lead.payload?.appointment;
  const clientSlots = lead.payload?.schedule || [];
  const showDecision =
    lead.type !== "service" && context === "leads" && ["novo", "em_contato"].includes(lead.status);

  return (
    <Dialog open={!!lead} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-h-[90vh] max-w-2xl overflow-y-auto rounded-2xl"
        data-testid="lead-detail-modal"
      >
        <button
          onClick={remove}
          title="Excluir chamado"
          aria-label="Excluir chamado"
          data-testid="detail-delete-button"
          className="absolute right-12 top-4 rounded-full p-2 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600"
        >
          <Trash2 className="h-4 w-4" />
        </button>
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-2 font-display text-2xl font-bold text-petrol">
            {displayName(lead)}
            <Badge className={typeMeta.classes}>{typeMeta.label}</Badge>
            {lead.type === "service" && lead.payload?.priority && (
              <Badge className={PRIORITY_COLORS[lead.payload.priority] || "bg-slate-100 text-slate-500"}>
                {lead.payload.priority}
              </Badge>
            )}
          </DialogTitle>
          <p className="font-mono text-[0.65rem] uppercase tracking-wider text-slate-400">
            {new Date(lead.created_at).toLocaleString("pt-BR", {
              dateStyle: "short",
              timeStyle: "short",
            })}
          </p>
        </DialogHeader>

        {lead.type === "service" && (
          <DemandFlow
            status={lead.status}
            history={lead.payload?.status_history || []}
            createdAt={lead.created_at}
          />
        )}

        {(lead.type === "service" || context === "leads") && (
          <div className="mt-2 flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
          {lead.type === "service" ? (
            <>
              <div className="space-y-1">
                <p className="text-[0.65rem] font-semibold uppercase tracking-wider text-slate-400">
                  Etapa
                </p>
                <Select value={lead.status} onValueChange={updateStatus}>
                  <SelectTrigger
                    className="h-9 w-[200px] rounded-full bg-white text-xs"
                    data-testid="detail-status-select"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STAGE_ORDER.map((k) => (
                      <SelectItem key={k} value={k}>
                        {STATUS_LABELS[k]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <p className="text-[0.65rem] font-semibold uppercase tracking-wider text-slate-400">
                  Tipo de serviço
                </p>
                <Select value={lead.payload?.service_type || ""} onValueChange={classify}>
                  <SelectTrigger
                    className={`h-9 w-[190px] rounded-full bg-white text-xs ${
                      !lead.payload?.service_type ? "border-dashed border-brand/50 text-brand" : ""
                    }`}
                    data-testid="detail-classify-select"
                  >
                    <SelectValue placeholder="Classificar…" />
                  </SelectTrigger>
                  <SelectContent>
                    {SERVICE_TYPES.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          ) : (
            <>
              <div className="space-y-1">
                <p className="text-[0.65rem] font-semibold uppercase tracking-wider text-slate-400">
                  Etapa
                </p>
                <Select
                  value={PARTNER_STAGES.includes(lead.status) ? lead.status : "novo"}
                  onValueChange={updateStatus}
                >
                  <SelectTrigger
                    className="h-9 w-[190px] rounded-full bg-white text-xs"
                    data-testid="detail-status-select"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PARTNER_STAGES.map((k) => (
                      <SelectItem key={k} value={k}>
                        {STATUS_LABELS[k]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {showDecision && (
                <div className="space-y-1" data-testid="detail-decision">
                  <p className="text-[0.65rem] font-semibold uppercase tracking-wider text-slate-400">
                    Decisão da parceria
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={accept}
                      data-testid="detail-accept-button"
                      className="inline-flex h-9 items-center gap-2 rounded-full bg-emerald-600 px-4 text-xs font-semibold text-white transition-colors hover:bg-emerald-700"
                    >
                      <CheckCircle2 className="h-4 w-4" />
                      Aceitar
                    </button>
                    <button
                      onClick={refuse}
                      data-testid="detail-refuse-button"
                      className="inline-flex h-9 items-center gap-2 rounded-full border border-red-200 px-4 text-xs font-semibold text-red-600 transition-colors hover:bg-red-50"
                    >
                      <XCircle className="h-4 w-4" />
                      Recusar
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
          </div>
        )}

        {lead.type === "partner" && context !== "leads" && (
          <PartnerEvaluations partner={lead} />
        )}

        {lead.type === "service" && <PricingBlock lead={lead} onChanged={onChanged} />}

        {lead.type === "service" && (
          <div className="mt-3 rounded-xl border border-slate-200 bg-white p-4" data-testid="appointment-block">
            <p className="text-[0.65rem] font-semibold uppercase tracking-wider text-slate-400">
              Agendamento da execução
            </p>
            {appointment && (
              <p className="mt-1.5 text-xs font-semibold text-emerald-600" data-testid="appointment-confirmed">
                Confirmado para {fmtSlot(appointment)}
              </p>
            )}
            {clientSlots.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {clientSlots.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => {
                      setApptDate(s.date);
                      setApptTime(s.time);
                    }}
                    data-testid={`detail-slot-chip-${i}`}
                    className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-[0.65rem] font-medium text-slate-600 transition-colors hover:border-brand hover:text-brand"
                  >
                    Opção do cliente: {String(s.date).split("-").reverse().join("/")} {s.time}
                  </button>
                ))}
              </div>
            )}
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <input
                type="date"
                value={apptDate}
                onChange={(e) => setApptDate(e.target.value)}
                data-testid="detail-appointment-date"
                className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/20"
              />
              <input
                type="time"
                value={apptTime}
                onChange={(e) => setApptTime(e.target.value)}
                data-testid="detail-appointment-time"
                className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/20"
              />
              <button
                onClick={saveAppointment}
                data-testid="detail-appointment-save"
                className="h-9 rounded-full bg-petrol px-4 text-xs font-semibold text-white transition-colors hover:bg-brand"
              >
                Confirmar agendamento
              </button>
            </div>
            <p className="mt-2 text-[0.65rem] text-slate-400">
              Só agende após orçamento aprovado e pago. Ao confirmar, a demanda vai para "Agendado"
              e entra na Agenda.
            </p>
          </div>
        )}

        {lead.type === "agency" && (
          <p className="mt-3 text-sm text-slate-600">
            Responsável: <strong className="text-petrol">{lead.payload?.name}</strong>
          </p>
        )}

        <dl className="mt-5 grid gap-x-8 gap-y-4 sm:grid-cols-2">
          {Object.entries(PAYLOAD_LABELS).map(([key, label]) => {
            const value = lead.payload?.[key];
            if (!value || (Array.isArray(value) && value.length === 0)) return null;
            const editable = EDITABLE_KEYS.has(key);
            return (
              <div key={key} className={key === "message" ? "sm:col-span-2" : ""}>
                <dt className="text-[0.65rem] font-semibold uppercase tracking-wider text-slate-400">
                  {label}
                </dt>
                <dd className="mt-1 text-sm leading-relaxed text-slate-700">
                  {editing === key ? (
                    <span className="flex items-center gap-2">
                      <input
                        autoFocus
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && saveField(key)}
                        data-testid={`detail-edit-input-${key}`}
                        placeholder={ARRAY_EDIT_KEYS.has(key) ? "Separe por vírgulas" : ""}
                        className="h-9 w-full rounded-lg border border-brand/50 bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-brand/30"
                      />
                      <button
                        onClick={() => saveField(key)}
                        aria-label="Salvar"
                        data-testid={`detail-edit-save-${key}`}
                        className="shrink-0 rounded-full bg-brand p-2 text-white hover:bg-brand-700"
                      >
                        <Check className="h-3.5 w-3.5" strokeWidth={3} />
                      </button>
                      <button
                        onClick={() => setEditing(null)}
                        aria-label="Cancelar"
                        className="shrink-0 rounded-full border border-slate-200 p-2 text-slate-400 hover:text-slate-600"
                      >
                        <X className="h-3.5 w-3.5" strokeWidth={3} />
                      </button>
                    </span>
                  ) : (
                    <span className="group/edit inline-flex items-start gap-1.5">
                      <span>{renderValue(value)}</span>
                      {editable && (
                        <button
                          onClick={() => startEdit(key, value)}
                          aria-label={`Editar ${label}`}
                          data-testid={`detail-edit-${key}`}
                          className="mt-0.5 shrink-0 rounded-full p-1 text-slate-300 opacity-0 transition-opacity hover:bg-brand/10 hover:text-brand group-hover/edit:opacity-100"
                        >
                          <Pencil className="h-3 w-3" />
                        </button>
                      )}
                    </span>
                  )}
                </dd>
              </div>
            );
          })}
        </dl>

        {attachments.length > 0 && (
          <div className="mt-5" data-testid="detail-attachments">
            <p className="text-[0.65rem] font-semibold uppercase tracking-wider text-slate-400">
              Anexos
            </p>
            <div className="mt-2 grid grid-cols-3 gap-2 sm:grid-cols-4">
              {attachments.map((att, i) => {
                const isImage = att.content_type?.startsWith("image/");
                const isVideo = att.content_type?.startsWith("video/");
                return (
                  <button
                    key={att.path}
                    onClick={() => openAttachment(att)}
                    title={`${att.name} — abrir em nova guia`}
                    data-testid={`detail-attachment-${i}`}
                    className="group relative aspect-square overflow-hidden rounded-xl border border-slate-200 bg-slate-50 transition-[border-color,box-shadow] duration-200 hover:border-brand hover:shadow-md"
                  >
                    {isImage ? (
                      <img
                        src={`${API_URL}/files/${att.path}`}
                        alt={att.name}
                        className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-105"
                      />
                    ) : isVideo ? (
                      <>
                        <video
                          src={`${API_URL}/files/${att.path}`}
                          muted
                          preload="metadata"
                          className="h-full w-full object-cover"
                        />
                        <span className="absolute inset-0 flex items-center justify-center bg-petrol/30">
                          <Play className="h-6 w-6 text-white" fill="currentColor" />
                        </span>
                      </>
                    ) : (
                      <span className="flex h-full w-full flex-col items-center justify-center gap-1 px-2 text-slate-500">
                        <Paperclip className="h-4 w-4" />
                        <span className="w-full truncate text-[0.6rem]">{att.name}</span>
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {lead.type === "service" && (
          <div className="mt-6 border-t border-slate-100 pt-5">
            {!matches ? (
              <button
                onClick={loadMatches}
                data-testid="detail-matches-toggle"
                className="inline-flex items-center gap-2 rounded-full bg-petrol px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-brand"
              >
                <Users className="h-3.5 w-3.5" />
                Ver parceiros compatíveis
              </button>
            ) : matches.loading ? (
              <div className="flex items-center gap-2 py-3 text-sm text-slate-400">
                <Loader2 className="h-4 w-4 animate-spin" />
                Buscando parceiros...
              </div>
            ) : (
              <div data-testid="detail-matches">
                <p className="text-[0.65rem] font-semibold uppercase tracking-wider text-slate-400">
                  Parceiros compatíveis ({matches.items.length})
                </p>
                {matches.items.length === 0 ? (
                  <p className="mt-2 rounded-xl border border-dashed border-slate-200 px-4 py-3 text-sm text-slate-500">
                    Nenhum parceiro compatível com a cidade e o tipo de serviço desta demanda
                    ainda.
                  </p>
                ) : (
                  <ul className="mt-2 space-y-2">
                    {matches.items.map((p) => (
                      <li
                        key={p.id}
                        className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-emerald-200 bg-emerald-50/60 px-4 py-3"
                        data-testid={`match-partner-${p.id}`}
                      >
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-petrol">
                            {p.payload?.name}
                            <span className="ml-2 font-mono text-[0.65rem] font-normal text-slate-500">
                              {p.payload?.phone}
                            </span>
                          </p>
                          <p className="mt-0.5 text-xs text-slate-500">
                            {(p.payload?.cities || []).join(", ")} — {p.payload?.state}
                          </p>
                        </div>
                        <button
                          onClick={() => indicate(p)}
                          disabled={indicating === p.id}
                          data-testid={`match-indicate-${p.id}`}
                          className="inline-flex shrink-0 items-center gap-2 rounded-full bg-[#25D366] px-4 py-2 text-xs font-bold text-white shadow-[0_8px_20px_-8px_rgba(37,211,102,0.6)] transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-[0_12px_24px_-8px_rgba(37,211,102,0.7)] disabled:opacity-60"
                        >
                          {indicating === p.id ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <WhatsAppIcon className="h-3.5 w-3.5" />
                          )}
                          Indicar
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            {lead.payload?.indicated_partner && (
              <div
                className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-emerald-200 bg-emerald-50/70 px-4 py-3"
                data-testid="indicated-partner"
              >
                <div className="min-w-0">
                  <p className="text-[0.65rem] font-semibold uppercase tracking-wider text-emerald-600">
                    Prestador indicado
                  </p>
                  <p className="mt-0.5 text-sm font-semibold text-petrol">
                    {lead.payload.indicated_partner.name}
                    <span className="ml-2 font-mono text-[0.65rem] font-normal text-slate-500">
                      {lead.payload.indicated_partner.phone}
                    </span>
                  </p>
                </div>
                <p className="text-[0.65rem] text-slate-400">
                  Indicado em{" "}
                  {new Date(lead.payload.indicated_partner.indicated_at).toLocaleString("pt-BR", {
                    dateStyle: "short",
                    timeStyle: "short",
                  })}
                </p>
              </div>
            )}
          </div>
        )}

        {lead.type === "service" && (
          <div
            className="mt-6 rounded-xl border border-slate-200 bg-white p-4"
            data-testid="internal-notes-block"
          >
            <p className="text-[0.65rem] font-semibold uppercase tracking-wider text-slate-400">
              Observações internas
            </p>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              data-testid="internal-notes-input"
              placeholder="Anotações livres da equipe — visíveis apenas aqui no painel (não aparecem no resumo pós-obra nem no orçamento)."
              className="mt-2 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/20"
            />
            <div className="mt-2 flex justify-end">
              <button
                onClick={saveNotes}
                disabled={savingNotes}
                data-testid="internal-notes-save"
                className="inline-flex h-8 items-center rounded-full bg-petrol px-4 text-xs font-semibold text-white transition-colors hover:bg-brand disabled:opacity-60"
              >
                {savingNotes ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Salvar observações"}
              </button>
            </div>
          </div>
        )}

        {lead.type === "service" && (
          <ExecutionBlock lead={lead} onChanged={onChanged} onOpenFile={openAttachment} />
        )}

        {lead.type === "service" && <EvaluationBlock lead={lead} onChanged={onChanged} />}
      </DialogContent>
    </Dialog>
  );
};
